import type {FsNodeDraft} from '@/services/auditService/auditAdjustOrchestrate';
import type {RoundingAdjustGroupVO} from '@/services/auditService/roundingAdjustService';
import {buildRoundingAdjustmentMaps} from '@/pages/components/fs-report/roundingAdjustHelpers';
import {
    plComputeComputedAmounts,
    recomputePlDerivedFormulaNodes,
    plIsProtectedParentHeader,
    resolvePlLeafAuditedValue,
    isPlCustomFsNode,
} from '@/pages/components/fs-report/FsFormula';
import {hasChildren} from '@/utils/fsDerived';
import {FS_CODE, findNodeByCodes, isCode} from '@/utils/fsCodes';
import {
    resolveBsInventoriesFromBsTree,
    shouldHidePlCostOfSalesRow,
    applyPlCotCosPurOverrides,
    resolvePlCotCosAmountForParentRollup,
    resolveRawCosForPlCotParentAuditedRollup,
} from '@/utils/plCostOfSalesVisibility';
import {useFsCategoryStore} from '@/store/audit/fsCategoryStore';

/** 与 ProfitLoss Audited / Audited(Round) PL-COT 主成本同口径（早上 PUR/COS 修复） */
function resolveDraftPlCotAuditedPrimaryCost(params: {
    cosOpening: number;
    purOpening: number;
    cosAuditedRaw: number;
    purAudited: number;
    openingInv: number;
    purchases: number;
    closingInv: number;
}): number {
    return resolvePlCotCosAmountForParentRollup({
        rawCos: resolveRawCosForPlCotParentAuditedRollup({
            cosOpening: params.cosOpening,
            purOpening: params.purOpening,
            cosAuditedRaw: params.cosAuditedRaw,
            purAudited: params.purAudited,
        }),
        openingInv: params.openingInv,
        purchases: params.purchases,
        closingInv: params.closingInv,
    });
}

function resolvePlCotOpeningsFromLegacy(plLegacy: any[]): {cosOpening: number; purOpening: number} {
    const cosNode = findNodeByCodes(plLegacy, ['PL-COT-COS']);
    const purNode = findNodeByCodes(plLegacy, ['PL-COT-PUR']);
    return {
        cosOpening: Number((cosNode as any)?.openingAmount ?? (cosNode as any)?.amount ?? 0) || 0,
        purOpening: Number((purNode as any)?.openingAmount ?? (purNode as any)?.amount ?? 0) || 0,
    };
}

function normalizeDraftCode(code: string | undefined | null): string {
    return String(code || '').trim().toUpperCase();
}

function resolveNodeCode(node: any): string {
    return normalizeDraftCode(node?.code || node?.scheduleCode);
}

function sumLeafAdjustments(node: any): number {
    if (!Array.isArray(node?.adjustment)) return 0;
    return node.adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
}

function sumAllAdjustments(node: any): number {
    if (!hasChildren(node)) {
        return sumLeafAdjustments(node);
    }
    let sum = 0;
    for (const child of node.children || []) {
        sum += sumAllAdjustments(child);
    }
    return sum;
}

function computeDerivedBsAdjTotal(item: any, fsTree: any[]): number | null {
    const CA = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_ASSETS);
    const CL = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_LIABILITIES);
    const NCA = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_ASSETS);
    const NCL = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_LIABILITIES);
    const getAdj = (node: any) => (node ? sumAllAdjustments(node) : 0);

    const netCurrentAdj = getAdj(CA) - getAdj(CL);
    if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) return netCurrentAdj;

    const netAssetsAdj = getAdj(NCA) + netCurrentAdj - getAdj(NCL);
    if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) return netAssetsAdj;

    return null;
}

/** PL adjustment 模式下的 unrounded Audited（与 ProfitLoss.getAuditedValue 一致） */
function computePlUnroundedByCode(plLegacy: any[], bsLegacy: any[]): Record<string, number> {
    const bsInventories = resolveBsInventoriesFromBsTree(bsLegacy);
    const hideCostOfSales = shouldHidePlCostOfSalesRow(bsInventories, plLegacy);
    const plCotInputMode = useFsCategoryStore.getState().plCotInputMode ?? 'COS';

    const leafClientByFsId: Record<number, number> = {};
    const leafByFsId: Record<number, number> = {};
    const visit = (nodes: any[]) => {
        for (const n of nodes || []) {
            if (Array.isArray(n?.children) && n.children.length > 0) {
                visit(n.children);
                continue;
            }
            if (plIsProtectedParentHeader(n)) continue;
            const base = Number(n?.openingAmount ?? n?.amount ?? 0) || 0;
            const fsId = Number(n.fsId);
            leafClientByFsId[fsId] = base;
            leafByFsId[fsId] = base + sumLeafAdjustments(n);
        }
    };
    visit(plLegacy);

    const oiNode = findNodeByCodes(plLegacy, ['PL-COT-OI']);
    const purNode = findNodeByCodes(plLegacy, ['PL-COT-PUR']);
    const ciNode = findNodeByCodes(plLegacy, ['PL-COT-CI']);
    const cosNode = findNodeByCodes(plLegacy, ['PL-COT-COS']);

    const oiId = Number((oiNode as any)?.fsId);
    const purId = Number((purNode as any)?.fsId);
    const ciId = Number((ciNode as any)?.fsId);
    const cosId = Number((cosNode as any)?.fsId);

    const rawCosOpening = cosNode && Number.isFinite(cosId)
        ? (Number((cosNode as any)?.openingAmount ?? (cosNode as any)?.amount ?? 0) || 0)
        : 0;
    const rawPurOpening = purNode && Number.isFinite(purId)
        ? (Number((purNode as any)?.openingAmount ?? (purNode as any)?.amount ?? 0) || 0)
        : 0;
    const purAdj = purNode ? sumLeafAdjustments(purNode) : 0;
    const cosAdj = cosNode ? sumLeafAdjustments(cosNode) : 0;

    applyPlCotCosPurOverrides({
        out: leafByFsId,
        cotInputMode: plCotInputMode,
        hideCostOfSalesRow: hideCostOfSales,
        bsLastYearAudited: bsInventories.lastYearAudited,
        bsClientCurrent: bsInventories.clientCurrent,
        oiId: Number.isFinite(oiId) ? oiId : undefined,
        purId: Number.isFinite(purId) ? purId : undefined,
        ciId: Number.isFinite(ciId) ? ciId : undefined,
        cosId: Number.isFinite(cosId) ? cosId : undefined,
        rawCosOpening,
        rawPurValue: rawPurOpening,
        purAdjustments: purAdj,
        ciAdjustments: ciNode ? sumLeafAdjustments(ciNode) : 0,
        cosAdjustments: cosAdj,
    });

    // 仅 PL-COT-PUR：隐藏 COS + COS 模式且有 PUR adjustment 时，避免伴生 COS adj 重复计入 Audited
    if (
        plCotInputMode === 'COS'
        && hideCostOfSales
        && purNode
        && Number.isFinite(purId)
        && Math.abs(purAdj) >= 0.01
    ) {
        leafByFsId[purId] = (leafClientByFsId[purId] ?? rawPurOpening) + purAdj;
    }

    // WHY: draft 叶子须与 ProfitLoss Audited 同口径（含 PL-AE-ADM+adjustByPaper → finalAmount）
    const applyLeafAuditedAligned = (nodes: any[]) => {
        for (const n of nodes || []) {
            if (Array.isArray(n?.children) && n.children.length > 0) {
                applyLeafAuditedAligned(n.children);
                continue;
            }
            if (plIsProtectedParentHeader(n)) continue;
            const fsId = Number(n.fsId);
            if (!Number.isFinite(fsId)) continue;
            leafByFsId[fsId] = resolvePlLeafAuditedValue(n, leafClientByFsId, leafByFsId);
        }
    };
    applyLeafAuditedAligned(plLegacy);

    const leafGetter = (node: any): number => {
        if (plIsProtectedParentHeader(node)) return 0;
        return resolvePlLeafAuditedValue(node, leafClientByFsId, leafByFsId);
    };
    const computedMap = plComputeComputedAmounts(plLegacy, leafGetter);

    const purNodeForMap = findNodeByCodes(plLegacy, ['PL-COT-PUR']);
    if (purNodeForMap) {
        computedMap[Number((purNodeForMap as any).fsId)] = Number(leafByFsId[Number((purNodeForMap as any).fsId)] ?? 0) || 0;
    }
    if (cosNode && Number.isFinite(cosId)) {
        computedMap[cosId] = Number(leafByFsId[cosId] ?? 0) || 0;
    }

    const sumNode = (node: any): number => {
        if (!node) return 0;
        if (plIsProtectedParentHeader(node)) return 0;
        if (!hasChildren(node)) return Number(leafByFsId[Number(node.fsId)] ?? 0) || 0;
        let sum = 0;
        for (const ch of (node.children || [])) sum += sumNode(ch);
        return sum;
    };

    const costTurnover = findNodeByCodes(plLegacy, FS_CODE.PL.COST_OF_TURNOVER);
    const costSales = findNodeByCodes(plLegacy, ['PL-COT-COS']);
    const openInv = findNodeByCodes(plLegacy, ['PL-COT-OI']);
    const closeInv = findNodeByCodes(plLegacy, ['PL-COT-CI']);
    const royalty = findNodeByCodes(plLegacy, ['PL-COT-ROY']);
    const subcontract = findNodeByCodes(plLegacy, ['PL-COT-SUB']);
    const otherCot = findNodeByCodes(plLegacy, ['PL-COT-OCT']);
    if (costTurnover) {
        let costSum = 0;
        const customCotSum = ((costTurnover as any).children || [])
            .filter((ch: any) => isPlCustomFsNode(ch))
            .reduce((s: number, ch: any) => s + sumNode(ch), 0);
        if (costSales || royalty || subcontract || otherCot || purNodeForMap || customCotSum) {
            const purAmt = purNodeForMap ? sumNode(purNodeForMap) : 0;
            const oiAmt = sumNode(openInv);
            const ciAmt = sumNode(closeInv);
            const cosLeafAudited = costSales ? sumNode(costSales) : 0;
            // Audited only：与 ProfitLoss.computedAuditedMap 同口径（PUR adj 伴生 COS → OI+PUR+CI）
            const cosAuditedRaw = Math.abs(cosLeafAudited) >= 0.01
                ? cosLeafAudited
                : (rawCosOpening + cosAdj);
            const primaryCost = resolveDraftPlCotAuditedPrimaryCost({
                cosOpening: rawCosOpening,
                purOpening: rawPurOpening,
                cosAuditedRaw,
                purAudited: purAmt,
                openingInv: oiAmt,
                purchases: purAmt,
                closingInv: ciAmt,
            });
            costSum = primaryCost + sumNode(royalty) + sumNode(subcontract) + sumNode(otherCot) + customCotSum;
        } else {
            costSum = sumNode(costTurnover);
        }
        computedMap[Number((costTurnover as any).fsId)] = costSum;
        // WHY: 覆盖 PL-COT 后同步 GP/GPOI/PBT/PAT（与 ProfitLoss 一致）
        recomputePlDerivedFormulaNodes(plLegacy, computedMap, leafGetter);
    }

    const byCode: Record<string, number> = {};
    const collect = (nodes: any[]) => {
        for (const node of nodes || []) {
            const code = resolveNodeCode(node);
            if (!code) continue;
            const isDerived = isCode(
                node,
                ...FS_CODE.PL.COST_OF_TURNOVER,
                ...FS_CODE.PL.GROSS,
                ...FS_CODE.PL.GROSS_AND_OTHER,
                ...FS_CODE.PL.BEFORE_TAX,
                ...FS_CODE.PL.AFTER_TAX,
            );
            const isLeaf = !hasChildren(node) && !plIsProtectedParentHeader(node) && !isDerived;
            byCode[code] = isLeaf
                ? (Number(leafByFsId[Number(node.fsId)] ?? 0) || 0)
                : (Number(computedMap[Number(node.fsId)] ?? 0) || 0);
            if (hasChildren(node)) collect(node.children);
        }
    };
    collect(plLegacy);
    return byCode;
}

/** BS adjustment 模式下的 unrounded Audited（与 BalanceSheet.getAuditedValue 一致） */
function computeBsUnroundedByCode(bsLegacy: any[], plUnrounded: Record<string, number>): Record<string, number> {
    const cache = new Map<string, number>();

    const getUnrounded = (node: any): number => {
        const code = resolveNodeCode(node);
        if (!code) return 0;
        if (cache.has(code)) return cache.get(code)!;

        let value: number;
        if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
            value = plUnrounded['PL-PAT'] ?? 0;
        } else if (isCode(node, ...FS_CODE.BS.CAPITAL_AND_RESERVES)) {
            const kids = Array.isArray(node?.children) ? node.children : [];
            value = kids.reduce((sum: number, child: any) => sum + getUnrounded(child), 0);
        } else {
            const clientVal = Number(node?.openingAmount ?? node?.amount ?? 0) || 0;
            const derivedAdj = computeDerivedBsAdjTotal(node, bsLegacy);
            const adjTotal = derivedAdj != null ? derivedAdj : sumAllAdjustments(node);
            value = clientVal + adjTotal;
        }

        cache.set(code, value);
        return value;
    };

    const byCode: Record<string, number> = {};
    const visit = (nodes: any[]) => {
        for (const node of nodes || []) {
            const code = resolveNodeCode(node);
            if (code) byCode[code] = getUnrounded(node);
            if (hasChildren(node)) visit(node.children);
        }
    };
    visit(bsLegacy);
    return byCode;
}

function findDraftNodeByCode(nodes: FsNodeDraft[], code: string): FsNodeDraft | null {
    const upper = normalizeDraftCode(code);
    for (const node of nodes || []) {
        if (normalizeDraftCode(node.code) === upper) return node;
        if (node.children?.length) {
            const found = findDraftNodeByCode(node.children, code);
            if (found) return found;
        }
    }
    return null;
}

function getDraftNodeAmount(draftNodes: FsNodeDraft[], code: string, field: 'finalAmount' | 'roundingAdjustment'): number {
    const node = findDraftNodeByCode(draftNodes, code);
    if (!node) return 0;
    if (field === 'roundingAdjustment') return Number(node.roundingAdjustment) || 0;
    return Number(node.finalAmount) || 0;
}

const PL_DERIVED_ROUND_SKIP = new Set([
    ...FS_CODE.PL.COST_OF_TURNOVER,
    ...FS_CODE.PL.GROSS,
    ...FS_CODE.PL.GROSS_AND_OTHER,
    ...FS_CODE.PL.BEFORE_TAX,
    ...FS_CODE.PL.AFTER_TAX,
].map(normalizeDraftCode));

const BS_DERIVED_ROUND_SKIP = new Set([
    ...FS_CODE.BS.NET_CURRENT,
    ...FS_CODE.BS.NET_ASSETS,
    FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0],
].map(normalizeDraftCode));

/**
 * 直接遍历 draft 树写入四舍五入结果（不依赖 legacy 树结构，避免子节点漏处理）。
 */
function applyRoundingToDraftTree(
    draftNodes: FsNodeDraft[],
    unroundedByCode: Record<string, number>,
    roundingMap: Record<string, number>,
    options?: {
        skipLeafCodes?: Set<string>;
        skipRoundingSumForCodes?: Set<string>;
    },
): void {
    const skipLeaf = options?.skipLeafCodes ?? new Set<string>();
    const skipRoundingSum = options?.skipRoundingSumForCodes ?? new Set<string>();

    const sumRounding = (draftNode: FsNodeDraft): number => {
        const code = normalizeDraftCode(draftNode.code);
        if (skipRoundingSum.has(code)) return 0;
        const children = draftNode.children ?? [];
        let sum = 0;
        for (const ch of children) sum += sumRounding(ch);
        if (roundingMap[code] != null) sum += roundingMap[code];
        return sum;
    };

    const visit = (draftNode: FsNodeDraft): void => {
        const code = normalizeDraftCode(draftNode.code);
        const children = draftNode.children ?? [];

        if (children.length > 0) {
            for (const ch of children) visit(ch);
            draftNode.roundingAdjustment = sumRounding(draftNode);
            const childSum = children.reduce((s, c) => s + (Number(c.finalAmount) || 0), 0);
            draftNode.finalAmount = childSum + (roundingMap[code] ?? 0);
            return;
        }

        if (skipLeaf.has(code)) return;

        const roundingAdj = roundingMap[code] ?? 0;
        const rawUnrounded = unroundedByCode[code];
        const unrounded = rawUnrounded != null ? rawUnrounded : (Number(draftNode.finalAmount) || 0);
        draftNode.roundingAdjustment = roundingAdj;
        draftNode.finalAmount = Math.round(unrounded) + roundingAdj;
    };

    for (const root of draftNodes) visit(root);
}

function sumPlCotCustomDraftAmounts(
    plNodes: FsNodeDraft[],
    field: 'finalAmount' | 'roundingAdjustment',
): number {
    const cot = findDraftNodeByCode(plNodes, 'PL-COT');
    if (!cot) return 0;
    let s = 0;
    for (const ch of (cot.children || []) as FsNodeDraft[]) {
        if (!isPlCustomFsNode(ch as any)) continue;
        s += field === 'roundingAdjustment'
            ? (Number((ch as any).roundingAdjustment) || 0)
            : (Number(ch.finalAmount) || 0);
    }
    return s;
}

function applyPlDerivedRoundedOverrides(
    plNodes: FsNodeDraft[],
    openings: {cosOpening: number; purOpening: number},
): void {
    const getFinal = (code: string) => getDraftNodeAmount(plNodes, code, 'finalAmount');
    const getRounding = (code: string) => getDraftNodeAmount(plNodes, code, 'roundingAdjustment');

    const customFinal = sumPlCotCustomDraftAmounts(plNodes, 'finalAmount');
    const customRounding = sumPlCotCustomDraftAmounts(plNodes, 'roundingAdjustment');

    const oiFinal = getFinal('PL-COT-OI');
    const purFinal = getFinal('PL-COT-PUR');
    const ciFinal = getFinal('PL-COT-CI');
    const cosFinal = getFinal('PL-COT-COS');
    // Audited(Round)：与 ProfitLoss.getRoundedAuditedValueLocal(PL-COT) 同口径
    const rawCosForParent = resolveRawCosForPlCotParentAuditedRollup({
        cosOpening: openings.cosOpening,
        purOpening: openings.purOpening,
        cosAuditedRaw: cosFinal,
        purAudited: purFinal,
    });
    const primaryCost = resolveDraftPlCotAuditedPrimaryCost({
        cosOpening: openings.cosOpening,
        purOpening: openings.purOpening,
        cosAuditedRaw: cosFinal,
        purAudited: purFinal,
        openingInv: oiFinal,
        purchases: purFinal,
        closingInv: ciFinal,
    });
    // 公式路径用 OI+PUR+CI 的 rounding；COS 录入路径用 COS rounding
    const primaryRounding = Math.abs(rawCosForParent) >= 0.01
        ? getRounding('PL-COT-COS')
        : getRounding('PL-COT-OI') + getRounding('PL-COT-PUR') + getRounding('PL-COT-CI');
    const costSum = primaryCost + getFinal('PL-COT-ROY') + getFinal('PL-COT-SUB')
        + getFinal('PL-COT-OCT') + customFinal;
    const costRounding = primaryRounding + getRounding('PL-COT-ROY')
        + getRounding('PL-COT-SUB') + getRounding('PL-COT-OCT') + customRounding;

    const setDerived = (code: string, finalAmount: number, roundingAdjustment: number) => {
        const node = findDraftNodeByCode(plNodes, code);
        if (node) {
            node.finalAmount = finalAmount;
            node.roundingAdjustment = roundingAdjustment;
        }
    };

    setDerived('PL-COT', costSum, costRounding + getRounding('PL-COT'));

    const gross = getFinal('PL-TURN') - costSum;
    const grossRounding = getRounding('PL-TURN') - costRounding;
    setDerived('PL-GP', gross, grossRounding + getRounding('PL-GP'));

    const grossAndOther = gross + getFinal('PL-OI');
    const gpoiRounding = grossRounding + getRounding('PL-OI');
    setDerived('PL-GPOI', grossAndOther, gpoiRounding + getRounding('PL-GPOI'));

    const beforeTax = grossAndOther - getFinal('PL-AE') - getFinal('PL-FC');
    const pbtRounding = gpoiRounding - getRounding('PL-AE') - getRounding('PL-FC');
    setDerived('PL-PBT', beforeTax, pbtRounding + getRounding('PL-PBT'));

    const afterTax = beforeTax - getFinal('PL-TAX');
    const patRounding = pbtRounding - getRounding('PL-TAX');
    setDerived('PL-PAT', afterTax, patRounding + getRounding('PL-PAT'));
}

function applyBsDerivedRoundedOverrides(bsNodes: FsNodeDraft[]): void {
    const caFinal = getDraftNodeAmount(bsNodes, 'BS-CA', 'finalAmount');
    const clFinal = getDraftNodeAmount(bsNodes, 'BS-CL', 'finalAmount');
    const ncaFinal = getDraftNodeAmount(bsNodes, 'BS-NCA', 'finalAmount');
    const nclFinal = getDraftNodeAmount(bsNodes, 'BS-NCL', 'finalAmount');

    const caRounding = getDraftNodeAmount(bsNodes, 'BS-CA', 'roundingAdjustment');
    const clRounding = getDraftNodeAmount(bsNodes, 'BS-CL', 'roundingAdjustment');
    const ncaRounding = getDraftNodeAmount(bsNodes, 'BS-NCA', 'roundingAdjustment');
    const nclRounding = getDraftNodeAmount(bsNodes, 'BS-NCL', 'roundingAdjustment');

    const setDerived = (code: string, finalAmount: number, roundingAdjustment: number) => {
        const node = findDraftNodeByCode(bsNodes, code);
        if (node) {
            node.finalAmount = finalAmount;
            node.roundingAdjustment = roundingAdjustment;
        }
    };

    const netCurrent = caFinal - clFinal;
    const netCurrentRounding = caRounding - clRounding;
    setDerived('BS-NCA-RES', netCurrent, netCurrentRounding);

    const netAssets = ncaFinal + netCurrent - nclFinal;
    const netAssetsRounding = ncaRounding + netCurrentRounding - nclRounding;
    setDerived('BS-NL-RES', netAssets, netAssetsRounding);
}

/** LFP 更新后，重算 BS-EQ 父节点（与 BalanceSheet 展示一致） */
function recomputeBsEqFromDraft(bsNodes: FsNodeDraft[], bsDisplayMap: Record<string, number>): void {
    const eqCode = FS_CODE.BS.CAPITAL_AND_RESERVES[0];
    const eqDraft = findDraftNodeByCode(bsNodes, eqCode);
    if (!eqDraft) return;

    const lfpCode = FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0];
    const skipRoundingSum = new Set([normalizeDraftCode(lfpCode)]);

    const sumRounding = (draftNode: FsNodeDraft): number => {
        const code = normalizeDraftCode(draftNode.code);
        if (skipRoundingSum.has(code)) return 0;
        const children = draftNode.children ?? [];
        let sum = 0;
        for (const ch of children) sum += sumRounding(ch);
        if (bsDisplayMap[code] != null) sum += bsDisplayMap[code];
        return sum;
    };

    const children = eqDraft.children ?? [];
    const directRounding = bsDisplayMap[normalizeDraftCode(eqCode)] ?? 0;
    eqDraft.roundingAdjustment = sumRounding(eqDraft);
    eqDraft.finalAmount = children.reduce((s, c) => s + (Number(c.finalAmount) || 0), 0) + directRounding;
}

/**
 * 将 draft 节点的 finalAmount 整理为与 Audited(Round) 列一致的值：
 * - 叶子：Math.round(unrounded) + rounding adjustment
 * - 父节点：子节点 round-then-sum + 节点自身 rounding adjustment
 * - 派生节点：按四舍五入后的组成部分重算公式
 * - roundingAdjustment：各节点子树 rounding 加总（与 BalanceSheet 一致）
 */
export function applyAuditedRoundToDraftNodes(
    bsLegacy: any[],
    plLegacy: any[],
    bsNodes: FsNodeDraft[],
    plNodes: FsNodeDraft[],
    roundingGroup: RoundingAdjustGroupVO | null | undefined,
    yearOfAssessment?: string | null,
): void {
    // 无 YOA：与改造前一致（单参 buildRoundingAdjustmentMaps）
    const yoa = String(yearOfAssessment || '').trim();
    const {bsDisplayMap, plDisplayMap} = yoa
        ? buildRoundingAdjustmentMaps(roundingGroup, {yearOfAssessment: yoa})
        : buildRoundingAdjustmentMaps(roundingGroup);

    const plUnrounded = computePlUnroundedByCode(plLegacy, bsLegacy);
    const bsUnrounded = computeBsUnroundedByCode(bsLegacy, plUnrounded);

    const lfpCode = normalizeDraftCode(FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0]);

    applyRoundingToDraftTree(bsNodes, bsUnrounded, bsDisplayMap, {
        skipLeafCodes: BS_DERIVED_ROUND_SKIP,
        skipRoundingSumForCodes: new Set([lfpCode]),
    });
    applyRoundingToDraftTree(plNodes, plUnrounded, plDisplayMap, {
        skipLeafCodes: PL_DERIVED_ROUND_SKIP,
    });

    // WHY: openings 须用 legacy opening（非已公式化的叶子），与 ProfitLoss rawCosClient 一致
    applyPlDerivedRoundedOverrides(plNodes, resolvePlCotOpeningsFromLegacy(plLegacy));

    const lfpNode = findDraftNodeByCode(bsNodes, FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0]);
    if (lfpNode) {
        lfpNode.finalAmount = getDraftNodeAmount(plNodes, 'PL-PAT', 'finalAmount');
        lfpNode.roundingAdjustment = bsDisplayMap[lfpCode] ?? 0;
    }

    recomputeBsEqFromDraft(bsNodes, bsDisplayMap);
    applyBsDerivedRoundedOverrides(bsNodes);
}

export {sumLeafAdjustments as draftSumLeafAdjustments};
